import {
  COMPETITION_GRADE_LABELS,
  PLANOGRAM_RANK_LABELS,
  type CompetitionGrade,
  type PlanogramGrade,
  type PlanogramRank,
} from '@fieldvision/contracts';

/*
 * `planogramTone(score)` WAS HERE AND IS GONE. It banded a raw number at 85 and
 * 60, and it was deleted rather than retuned for three reasons.
 *
 * ITS CUT POINTS DESCRIBED A SCALE THAT NO LONGER EXISTS. A scored photo now
 * floors at 50, so the `danger` branch fired almost never; 85 sat inside GOLD,
 * cutting a band in half. A number coloured against thresholds from a superseded
 * formula is worse than an uncoloured number, because it looks authoritative.
 *
 * A SERVER CLASSIFICATION EXISTS NOW, and the rule `competitionGradeTone` states
 * at length applies verbatim: the console renders the rank the server sent and
 * must never re-derive one, however convenient that looks.
 *
 * AND IT WAS APPLIED TO FOUR DIFFERENT GRAINS — a photo total, a per-SKU score,
 * a SKU average and an outlet average — of which only the first two have a
 * verdict to render. One shared function across all four guaranteed that the
 * other two got an invented one. An AVERAGE HAS NO RANK: a DIAMOND photo and a
 * COAL photo mean to 75, which is SILVER, a band neither of them held.
 */

/**
 * The tone for a rank the SERVER computed. Never derived from a number here.
 *
 * DIAMOND and GOLD share `success` deliberately. The available tones are
 * `neutral | success | warn | danger | info`, and `info` is this console's
 * CATEGORICAL tone — the "Internal" pill, the "Sistem" badge, the role chip.
 * Spending it on the second rung of an ordinal ladder would make GOLD read as a
 * type rather than a standing. Design guidelines §11.4 require every badge to
 * carry a word, and the word is what separates them; the hue only has to
 * separate "on plan" from "a finding" from "a failure".
 *
 * Note this is the opposite call from `competitionGradeTone`, which paints its
 * best band neutral. There, GOOD is the expected state and green would have
 * hidden VERY_GOOD down a long table. Here the business target sits mid-GOLD, so
 * GOLD *is* success and DIAMOND is above target rather than different news.
 */
export function planogramRankTone(
  rank: PlanogramRank | null | undefined,
): 'success' | 'neutral' | 'warn' | 'danger' | null {
  if (!rank) return null;
  switch (rank) {
    case 'DIAMOND':
    case 'GOLD':
      return 'success';
    case 'SILVER':
      return 'neutral';
    case 'BRONZE':
      return 'warn';
    case 'COAL':
      return 'danger';
  }
}

/** The rank's label, or null so the caller can render the legacy chip instead. */
export function planogramRankLabel(rank: PlanogramRank | null | undefined): string | null {
  return rank ? PLANOGRAM_RANK_LABELS[rank] : null;
}

/**
 * The tone for one metric's letter grade.
 *
 * C is `warn` and not `danger`, for the reason `NEED_ADJUSTMENT` is: a
 * mis-shelved product is a merchandising finding, not a broken system, and
 * `danger` stays reserved for the latter. `success` is reserved for A alone —
 * B means "exactly on plan", which is fine and is not an achievement.
 */
export function planogramGradeTone(
  grade: PlanogramGrade | null | undefined,
): 'success' | 'neutral' | 'warn' | null {
  if (!grade) return null;
  switch (grade) {
    case 'A':
      return 'success';
    case 'B':
      return 'neutral';
    case 'C':
      return 'warn';
  }
}

/**
 * Which of the four states a planogram number is in — and NOT what it is worth.
 *
 * A CLASSIFIER, NOT A BAND. It reads nullity and counts only; it never looks at
 * the score's value. That is what makes it safe where `planogramTone` was not.
 *
 * The states, and why there are four rather than two. `columns.tsx` already
 * insists on two kinds of blank ("not on the plan" versus "nobody photographed
 * it") because collapsing them turns a deliberate exclusion into a coverage gap.
 * The formula change adds a third: a row that WAS scored, whose number is right
 * there, and which carries no letters because it predates letter grading. An
 * em-dash there would say "not measured" about a measurement in the same row.
 *
 * A pure function rather than logic inside a `cell` renderer because
 * `manager-web` has no DOM test environment — this is the only shape in which
 * the rule is testable, and it is the part that will be got wrong.
 */
export function planogramScoreState(input: {
  scoredRows: number;
  gradedRows: number;
}): 'none' | 'legacy' | 'mixed' | 'graded' {
  if (input.scoredRows === 0) return 'none';
  if (input.gradedRows === 0) return 'legacy';
  if (input.gradedRows < input.scoredRows) return 'mixed';
  return 'graded';
}

/**
 * Attainment against a target, as a badge tone.
 *
 * `neutral` for the 80–99 band rather than `warn`: a team at 94% of plan is not
 * in a warning state, and painting it amber is the kind of thing that makes a
 * dashboard get ignored.
 */
export function attainmentTone(
  percentage: string | number | null | undefined,
): 'success' | 'neutral' | 'warn' | null {
  if (percentage === null || percentage === undefined || percentage === '') return null;
  const value = Number(percentage);
  if (!Number.isFinite(value)) return null;
  if (value >= 100) return 'success';
  if (value >= 80) return 'neutral';
  return 'warn';
}

/**
 * Facing attainment on the planogram report — `sum(actual) / sum(target)`.
 *
 * NOT `attainmentTone`, and the difference is the point rather than an oversight.
 * That one treats anything at or above 100% as success, which is right for visits
 * against a plan: doing more of them is doing better. This ratio is UNCAPPED and
 * OVER-FACING IS NOT COMPLIANCE — a product given twice its planned shelf space
 * is taking space the plan assigned to something else, so far above 100 is a
 * finding, not a win. `ai-svc`'s per-SKU score caps its facing term at 1.0 for
 * the same reason; this column deliberately does not, so that the overshoot stays
 * visible instead of being silently rounded into "perfect".
 *
 * The upper band is generous: shelves are counted from photographs, and a couple
 * of extra facings is measurement noise rather than a merchandising decision.
 */
export function facingAttainmentTone(
  percentage: string | number | null | undefined,
): 'success' | 'neutral' | 'warn' | 'danger' | null {
  if (percentage === null || percentage === undefined || percentage === '') return null;
  const value = Number(percentage);
  if (!Number.isFinite(value)) return null;
  if (value > 125) return 'warn';
  if (value >= 90) return 'success';
  if (value >= 60) return 'neutral';
  return 'danger';
}

/*
 * A `PRICE_INDEX_BAND = 3` and its `priceIndexTone` used to sit here — a ±3 band
 * around 100 for the list-price index, deliberately kept apart from the FR-14
 * grading band below. That metric is retired and both are gone. The competition
 * band that remains is administrable and classified server-side, which is the
 * arrangement this file's header argues for anyway.
 */

/**
 * A competition plan's grade, as a badge tone.
 *
 * A LOOKUP, NOT A CALCULATION — and that is the whole point of this pair of
 * functions. A `competitionIndexTone(actual, target)` used to live here and
 * classified in the browser, against a symmetric ±5% tolerance, with this
 * comment on the epsilon it needed:
 *
 *   > THE EPSILON IS NOT PADDING. The server counts `plansOffPlan` with the same
 *   > constant but in PostgreSQL `numeric`, which is exact decimal; this runs in
 *   > IEEE-754 doubles, where `1.05 / 1.0 - 1` is 0.050000000000000044 rather
 *   > than 0.05. A plan landing exactly on the band would then be inside the band
 *   > on the server and outside it here, and the meter's headline count would
 *   > contradict the row colours directly beneath it.
 *
 * That reasoning is still correct and it is now UNANSWERABLE in this file. The
 * grading rule has two bounds whose inclusive sides point in OPPOSITE directions
 * (`<=` below, `>=` above), so slack that rescues the lower boundary breaks the
 * upper one — there is no single epsilon, and the quantity compared scales with
 * each plan's target. So the classification moved into PostgreSQL, where the
 * comparison is exact decimal: `competition_grade()`, created by migration 0023,
 * with `packages/db/tests/pgtap/competition_grade.sql` now owning the boundary
 * property this file's deleted test used to.
 *
 * The console renders `row.grade`. It must never re-derive one from `actualIndex`
 * and `targetIndex`, however convenient that looks.
 *
 * TONES. `GOOD` is `neutral` rather than green: it is the expected state, and
 * painting it success makes `VERY_GOOD` indistinguishable at a glance down a
 * 25-row table, which is the only job the column has. `NEED_ADJUSTMENT` is
 * `warn` and not `danger` — it is a commercial finding, not a broken system, and
 * `danger` stays reserved for the latter.
 *
 * Null for a plan with no grade, so the caller renders an em-dash. A plan nobody
 * has priced is not a plan that is failing.
 */
export function competitionGradeTone(
  grade: CompetitionGrade | null | undefined,
): 'success' | 'neutral' | 'warn' | null {
  switch (grade) {
    case 'VERY_GOOD':
      return 'success';
    case 'GOOD':
      return 'neutral';
    case 'NEED_ADJUSTMENT':
      return 'warn';
    default:
      return null;
  }
}

/**
 * The grade in the product owner's own words.
 *
 * From `packages/contracts` rather than restated here, so the console and the
 * XLSX/PDF exports cannot drift on vocabulary the product owner defined. An
 * export whose wording disagrees with the screen it came from is a support call.
 */
export function competitionGradeLabel(grade: CompetitionGrade | null | undefined): string | null {
  return grade ? COMPETITION_GRADE_LABELS[grade] : null;
}

/**
 * The band bounds, for a LEGEND — never for a verdict.
 *
 * The settings form and the price-index page both print "Sangat baik ≤ 0,96×"
 * beside the badges, and that text has to come from somewhere. Computing the
 * DISPLAYED bounds in JavaScript is safe in a way computing the VERDICT is not:
 * a bound rendered to two decimals cannot disagree with the server about which
 * side of itself a plan falls on, because it decides nothing.
 */
export function competitionBands(
  target: number,
  threshold: number,
): { lower: number; upper: number } {
  return { lower: target * (1 - threshold), upper: target * (1 + threshold) };
}

/* -------------------------------------------------------------------------- */
/* System health — INF-04                                                      */
/* -------------------------------------------------------------------------- */

/**
 * A dependency boolean, as a badge tone.
 *
 * TWO TONES AND NO MIDDLE, which is the point: `/system/health` reports whether
 * a dependency ANSWERED, and there is no partial answer. An amber state here
 * would be the screen inventing a nuance the server did not report.
 */
export function dependencyTone(answered: boolean): 'success' | 'danger' {
  return answered ? 'success' : 'danger';
}

/**
 * The worker heartbeat's age, as a tone.
 *
 * THE NUMBERS COME FROM THE WRITER, not from taste. `worker-heartbeat.service.ts`
 * beats every 30s on a 180s TTL — six beats — so anything under a minute is
 * simply the current beat, and past ~90s beats are being missed while the key is
 * still alive. That window is the only place a tone can say something the
 * `alive` boolean has not already said, which is why this is amber-only: once
 * the key expires, `dependencies.worker` has gone false and the dependency badge
 * is the honest place for it to be red.
 *
 * Null for a heartbeat whose payload would not parse — the probe deliberately
 * still counts that as alive, and colouring an unknown age would be a claim.
 */
export function heartbeatTone(
  ageSeconds: number | null | undefined,
): 'success' | 'warn' | null {
  if (ageSeconds === null || ageSeconds === undefined) return null;
  if (!Number.isFinite(ageSeconds)) return null;
  return ageSeconds <= 90 ? 'success' : 'warn';
}

/**
 * Queue depth, as a tone — and DELIBERATELY ALWAYS NEUTRAL FOR `waiting`.
 *
 * This is the one threshold helper in this file that refuses to have a
 * threshold, and the refusal is the design. A backlog is a load condition: 500
 * photos at 09:00 is a Tuesday and the same number at 03:00 is an incident, so
 * any constant here would be wrong twice a day and would train an administrator
 * to ignore the colour. The api makes the same refusal — queue state never
 * touches `status` — and the two must agree.
 *
 * `failed` is different in kind and is the one thing here that does colour: a
 * failed job is a defect, not load, and it reads the same at every hour.
 */
export function failedJobsTone(failed: number): 'neutral' | 'danger' {
  return failed > 0 ? 'danger' : 'neutral';
}
